/**
 * NOT-001C2A · Filter-chip label mapping for the Operations
 * Notification Centre.
 *
 * Pure helper — kept in its own module so a deterministic Jest test
 * can prove the mapping never leaks internal parameter names, UUIDs
 * or raw IDs when a human-readable label is available.
 *
 * Contract
 * --------
 *   * Input:  URL filter key (e.g. `project_id`) + raw value +
 *             the `opts` object returned by `/api/notifications/
 *             foundation/filters`.
 *   * Output: `{ label, valueLabel }` where `label` is the visible
 *             field caption (e.g. "Project") and `valueLabel` is the
 *             human-readable rendition of the value (e.g.
 *             "UAT-C2-1 · UAT C2 Customer"). If no registry entry
 *             matches, we fall back to a truncated ID hint rather
 *             than dumping the full UUID.
 *   * The underlying URL value stays untouched — the caller uses
 *             it as-is to drive `removeChip`.
 */

const FIELD_LABELS = {
    since: "From",
    until: "To",
    event_type: "Event",
    template_key: "Template",
    source: "Source",
    submission_status: "Result",
    severity: "Severity",
    customer_id: "Customer",
    project_id: "Project",
    recipient_id: "Recipient",
    recipient_role: "Role",
    correlation_id: "Correlation",
    appointment_id: "Appointment",
    error_code: "Reason",
    channel: "Channel",
    status: "State",
    retryable: "Retryable",
    q: "Search",
};

// Which opts collection to look each key up in.
const REGISTRY_FOR_KEY = {
    event_type:         "event_types",
    template_key:       "event_types",
    source:             "sources",
    submission_status:  "submission_statuses",
    severity:           "severities",
    customer_id:        "customers",
    project_id:         "projects",
    recipient_role:     "recipient_roles",
    error_code:         "reason_codes",
    channel:            "channels",
    status:             "delivery_states",
};

function _lookupLabel(opts, registryKey, rawValue) {
    const list = (opts && opts[registryKey]) || [];
    for (const opt of list) {
        if (opt && String(opt.value) === String(rawValue)) {
            return opt.label || null;
        }
    }
    return null;
}

function _shortId(v) {
    // Fallback rendition when no registry label exists — keep the
    // final 6 chars so operators can correlate with the URL without
    // seeing an ugly UUID.
    const s = String(v || "");
    if (s.length <= 12) return s;
    return `…${s.slice(-6)}`;
}

function _formatDate(v) {
    // Datetimes arrive as either `YYYY-MM-DDTHH:mm` or full ISO.
    if (!v) return "";
    try {
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return String(v);
        return d.toLocaleString(undefined, {
            year: "numeric", month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return String(v); }
}

/**
 * Return the `{ label, valueLabel }` a filter chip should render.
 *
 * @param {string} key - URL filter key (e.g. `project_id`).
 * @param {string} value - the raw filter value.
 * @param {object} [opts] - filter option registry from the API.
 * @returns {{label:string, valueLabel:string}}
 */
export function chipMeta(key, value, opts) {
    const label = FIELD_LABELS[key] || key;

    if (key === "since" || key === "until") {
        return { label, valueLabel: _formatDate(value) };
    }

    if (key === "retryable") {
        return {
            label,
            valueLabel: String(value) === "true" ? "Retryable only" : String(value),
        };
    }

    const registryKey = REGISTRY_FOR_KEY[key];
    if (registryKey) {
        const found = _lookupLabel(opts, registryKey, value);
        if (found) return { label, valueLabel: found };
        // No registry match — the ID is meaningless to a human, so
        // fall back to a short trailing hint.
        return { label, valueLabel: _shortId(value) };
    }

    // Keys without a registry (`correlation_id`, `recipient_id`,
    // `appointment_id`, `q`) — never expose the full UUID; free-text
    // search stays as-is.
    if (key === "q") return { label, valueLabel: String(value) };
    if (key.endsWith("_id")) return { label, valueLabel: _shortId(value) };
    return { label, valueLabel: String(value) };
}

// Exposed for the focused test suite.
export const _INTERNALS = { FIELD_LABELS, REGISTRY_FOR_KEY };
